import { useEffect, useState } from 'react';
import { 
  Box, 
  Text, 
  Loader, 
  Grid, 
  Badge, 
  Accordion, 
  Checkbox, 
  Group,
  Paper,
  ScrollArea,
  TextInput,
  Stack
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { apiInstance } from '../../services/api_service';
import { groupPermissionsByModel } from '../../utils/permissions';
import { toast } from 'react-toastify';

const UserDetails = ({ userId }) => {
  const [userDetails, setUserDetails] = useState(null);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissionSearch, setPermissionSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [loadingPermissions, setLoadingPermissions] = useState(new Set());

  // Helper function to get permission display name
  const getPermissionDisplayName = (permission) => {
    // Use actual_description if available, otherwise fall back to name
    return permission.actual_description || permission.name || permission.codename;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch user details, available permissions, and available groups in parallel
        const [userResponse, permissionsResponse, groupsResponse] = await Promise.all([
          apiInstance.get(`authentication/users/${userId}/data/`),
          apiInstance.get('authentication/permissions/'),
          apiInstance.get('authentication/groups/')
        ]);

        setUserDetails(userResponse.data);
        setAvailablePermissions(permissionsResponse.data);
        setAvailableGroups(groupsResponse.data);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Erro ao carregar dados');
        toast.error('Erro ao carregar dados do usuário');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  const handlePermissionToggle = async (permission, isChecked) => {
    const permissionKey = permission.codename;
    
    // Add to loading state
    setLoadingPermissions(prev => new Set([...prev, permissionKey]));
    
    try {
      if (isChecked) {
        // Add permission
        await apiInstance.post(`authentication/users/${userId}/grant-permission/`, {
          permission_id: permission.id,
          codename: permission.codename,
          content_type: permission.content_type
        });
        
        // Update local state
        setUserDetails(prev => ({
          ...prev,
          user_permissions: [...prev.user_permissions, permission]
        }));
        
        toast.success(
          `Permissão "${getPermissionDisplayName(permission)}" adicionada`,
          {
            position: "top-right",
            autoClose: 1500,
          }
        );
      } else {
        // Remove permission
        await apiInstance.delete(`authentication/users/${userId}/revoke-permission/`, {
          data: {
            permission_id: permission.id,
            codename: permission.codename,
            content_type: permission.content_type
          }
        });
        
        // Update local state
        setUserDetails(prev => ({
          ...prev,
          user_permissions: prev.user_permissions.filter(p => p.codename !== permission.codename)
        }));
        
        toast.success(
          `Permissão "${getPermissionDisplayName(permission)}" removida`,
          {
            position: "top-right",
            autoClose: 1500,
          }
        );
      }
    } catch (err) {
      console.error('Error toggling permission:', err);
      toast.error(`Erro ao ${isChecked ? 'adicionar' : 'remover'} permissão`);
    } finally {
      // Remove from loading state
      setLoadingPermissions(prev => {
        const newSet = new Set(prev);
        newSet.delete(permissionKey);
        return newSet;
      });
    }
  };

  const handleGroupToggle = async (group, isChecked) => {
    try {
      if (isChecked) {
        // Add group
        await apiInstance.post(`authentication/users/${userId}/add-to-group/`, {
          group_id: group.id
        });
        
        // Update local state
        setUserDetails(prev => ({
          ...prev,
          groups: [...prev.groups, group]
        }));
        
        toast.success(
          `Grupo "${group.name}" adicionado`, {
            position: "top-right",
            autoClose: 1500,
          }
        );
      } else {
        // Remove group
        await apiInstance.delete(`authentication/users/${userId}/remove-from-group/`, {
          data: {
            group_id: group.id
          }
        });
        
        // Update local state
        setUserDetails(prev => ({
          ...prev,
          groups: prev.groups.filter(g => g.id !== group.id)
        }));
        
        toast.success(`Grupo "${group.name}" removido`, {
          position: "top-right",
          autoClose: 1500,
        });
      }
    } catch (err) {
      console.error('Error toggling group:', err);
      toast.error(`Erro ao ${isChecked ? 'adicionar' : 'remover'} grupo`);
    }
  };

  const isPermissionChecked = (permission) => {
    return userDetails?.user_permissions?.some(p => p.codename === permission.codename) || false;
  };

  const isGroupChecked = (group) => {
    return userDetails?.groups?.some(g => g.id === group.id) || false;
  };

  const filteredPermissions = availablePermissions.filter(permission =>
    getPermissionDisplayName(permission).toLowerCase().includes(permissionSearch.toLowerCase()) ||
    permission.codename.toLowerCase().includes(permissionSearch.toLowerCase()) ||
    `${permission.content_type.app_label}.${permission.content_type.model}`.toLowerCase().includes(permissionSearch.toLowerCase())
  );

  const filteredGroups = availableGroups.filter(group =>
    group.name.toLowerCase().includes(groupSearch.toLowerCase())
  );

  const groupedAvailablePermissions = groupPermissionsByModel(filteredPermissions);

  if (isLoading) {
    return (
      <Box 
        sx={{
          width: '100%',
          minWidth: '800px',
          '@media (max-width: 768px)': {
            minWidth: '100%',
          }
        }}
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <Loader size="sm" />
        <Text ml="md">Carregando dados...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        sx={{
          width: '100%',
          minWidth: '800px',
          '@media (max-width: 768px)': {
            minWidth: '100%',
          }
        }}
        p="md">
        <Text color="red" weight={500}>{error}</Text>
      </Box>
    );
  }

  if (!userDetails) {
    return (
      <Box 
        sx={{
          width: '100%',
          minWidth: '800px',
          '@media (max-width: 768px)': {
            minWidth: '100%',
          }
        }}
        p="md">
        <Text>Nenhum dado encontrado para este usuário.</Text>
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        width: '100%',
        minWidth: '800px',
        maxWidth: '100%',
        padding: '16px',
        '@media (max-width: 768px)': {
          minWidth: '100%',
          padding: '8px',
        }
      }}
    >
      <Stack spacing="lg">
        {/* User Information Section */}
        <Paper p="md" withBorder>
          <Text weight={500} size="lg" mb="sm">
            Informações do Usuário
          </Text>
          
          <Grid 
            gutter="md"
            sx={{
              '@media (max-width: 768px)': {
                '& > div': {
                  width: '100% !important',
                  maxWidth: '100% !important',
                }
              }
            }}
          >
            <Grid.Col 
              span={6}
              sx={{
                '@media (max-width: 768px)': {
                  span: 12,
                }
              }}
            >
              <Text weight={400} size="sm" color="gray">Email:</Text>
              <Text weight={500} mb="xs">{userDetails.email || 'N/A'}</Text>
            </Grid.Col>
            <Grid.Col 
              span={3}
              sx={{
                '@media (max-width: 768px)': {
                  span: 12,
                }
              }}
            >
              <Text weight={400} size="sm" color="gray">Nome:</Text>
              <Text weight={500} mb="xs">{userDetails.first_name || 'N/A'}</Text>
            </Grid.Col>
            <Grid.Col 
              span={3}
              sx={{
                '@media (max-width: 768px)': {
                  span: 12,
                }
              }}
            >
              <Text weight={400} size="sm" color="gray">Sobrenome:</Text>
              <Text weight={500} mb="xs">{userDetails.last_name || 'N/A'}</Text>
            </Grid.Col>
          </Grid>
        </Paper>

        {/* Groups and Permissions Management Side by Side */}
        <Grid 
          gutter="lg"
          sx={{
            '@media (max-width: 768px)': {
              '& > div': {
                width: '100% !important',
                maxWidth: '100% !important',
              }
            }
          }}
        >
          {/* Groups Management Section */}
          <Grid.Col 
            span={6}
            sx={{
              '@media (max-width: 768px)': {
                span: 12,
              }
            }}
          >
            <Paper p="md" withBorder style={{ height: '100%' }}>
              <Text weight={500} size="lg" mb="sm">
                Gerenciamento de Grupos
              </Text>
              
              <TextInput
                placeholder="Pesquisar grupos..."
                icon={<IconSearch size={16} />}
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
                mb="md"
                size="sm"
              />

              <Grid
                sx={{
                  '@media (max-width: 768px)': {
                    '& > div': {
                      width: '100% !important',
                      maxWidth: '100% !important',
                    }
                  }
                }}
              >
                <Grid.Col 
                  span={6}
                  sx={{
                    '@media (max-width: 768px)': {
                      span: 12,
                      marginBottom: '16px',
                    }
                  }}
                >
                  <Text weight={500} size="sm" mb="sm" color="blue">
                    Grupos Disponíveis
                  </Text>
                  <ScrollArea style={{ height: '300px' }}>
                    <Stack spacing="xs">
                      {filteredGroups.map((group) => (
                        <Checkbox
                          key={group.id}
                          label={group.name}
                          checked={isGroupChecked(group)}
                          onChange={(event) => handleGroupToggle(group, event.currentTarget.checked)}
                          size="xs"
                        />
                      ))}
                    </Stack>
                  </ScrollArea>
                </Grid.Col>
                
                <Grid.Col 
                  span={6}
                  sx={{
                    '@media (max-width: 768px)': {
                      span: 12,
                    }
                  }}
                >
                  <Text weight={500} size="sm" mb="sm" color="green">
                    Grupos Atribuídos ({userDetails.groups?.length || 0})
                  </Text>
                  <ScrollArea style={{ height: '300px' }}>
                    {userDetails.groups && userDetails.groups.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {userDetails.groups.map((group) => (
                          <Badge
                            key={group.id}
                            variant="filled"
                            color="blue"
                            size="sm"
                            style={{ marginBottom: '4px' }}
                          >
                            {group.name}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <Text color="gray" style={{ fontStyle: 'italic' }} size="xs">
                        Não pertence a nenhum grupo
                      </Text>
                    )}
                  </ScrollArea>
                </Grid.Col>
              </Grid>
            </Paper>
          </Grid.Col>

          {/* Permissions Management Section */}
          <Grid.Col 
            span={6}
            sx={{
              '@media (max-width: 768px)': {
                span: 12,
              }
            }}
          >
            <Paper p="md" withBorder style={{ height: '100%' }}>
              <Group position="apart" mb="sm">
                <Text weight={500} size="lg">
                  Gerenciamento de Permissões
                </Text>
                <Badge size="sm" variant="light" color="blue">
                  {userDetails.user_permissions?.length || 0} atribuídas
                </Badge>
              </Group>
              
              <TextInput
                placeholder="Pesquisar permissões..."
                icon={<IconSearch size={14} />}
                value={permissionSearch}
                onChange={(e) => setPermissionSearch(e.target.value)}
                mb="md"
                size="sm"
              />

              <ScrollArea style={{ height: '500px' }}>
                <Accordion variant="separated" size="sm">
                  {Object.entries(groupedAvailablePermissions).map(([modelKey, group]) => (
                    <Accordion.Item key={modelKey} value={modelKey}>
                      <Accordion.Control>
                        <Group position="apart">
                          <Text weight={500} size="sm">
                            {group.model_name}
                          </Text>
                          <Group spacing={8}>
                            <Badge size="xs" variant="light" color="green">
                              {group.permissions.filter(p => isPermissionChecked(p)).length}
                            </Badge>
                            <Text size="xs" color="gray">
                              / {group.permissions.length}
                            </Text>
                          </Group>
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack spacing={4}>
                          {group.permissions.map((permission) => {
                            const isLoading = loadingPermissions.has(permission.codename);
                            
                            return (
                              <Group key={permission.codename} spacing={8} style={{ alignItems: 'center' }}>
                                {isLoading ? (
                                  <Loader size="xs" />
                                ) : (
                                  <Checkbox
                                    checked={isPermissionChecked(permission)}
                                    onChange={(event) => handlePermissionToggle(permission, event.currentTarget.checked)}
                                    size="xs"
                                  />
                                )}
                                <Text size="xs" weight={500} style={{ lineHeight: 1.2 }}>
                                  {getPermissionDisplayName(permission)}
                                </Text>
                              </Group>
                            );
                          })}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </ScrollArea>
            </Paper>
          </Grid.Col>
        </Grid>
      </Stack>
    </Box>
  );
};

export default UserDetails; 